import type { paths } from '@ccp/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, expectBody } from '../../api/client.js';

/**
 * The contact behind a call, as the API answers it.
 *
 * Read by the panel beside the interaction rather than carried on the interaction: a contact is the
 * contacts context's, it outlives any one call, and the same person may be on the phone twice in an
 * afternoon. One query, cached by id, serves both.
 */
type Json<T> = T extends { content: { 'application/json': infer B } } ? B : never;

type Body<T> = T extends { requestBody: { content: { 'application/json': infer B } } } ? B : never;

export type Contact = Json<paths['/v1/contacts/{id}']['get']['responses'][200]>;
export type Organisation = Json<
  paths['/v1/organisations']['get']['responses'][200]
>['items'][number];
export type AttachIdentityInput = Body<paths['/v1/contacts/{id}/identities']['post']>;
export type AssignOrganisationInput = Body<paths['/v1/contacts/{id}/organisation']['post']>;
export type MergeSuggestions = Json<paths['/v1/contacts/{id}/merges']['get']['responses'][200]>;

export const contactKeys = {
  all: ['contacts'] as const,
  search: (query: string) => ['contacts', 'search', query] as const,
  one: (id: string) => ['contacts', id] as const,
  merges: (id: string) => ['contacts', id, 'merges'] as const,
  organisations: ['organisations'] as const,
};

export function useContact(contactId: string | undefined) {
  return useQuery({
    queryKey: contactKeys.one(contactId ?? 'none'),
    enabled: contactId !== undefined,
    queryFn: async () =>
      expectBody(
        await api.GET('/v1/contacts/{id}', { params: { path: { id: contactId as string } } }),
      ),
  });
}

/**
 * The duplicates somebody has suggested for this contact.
 *
 * A suggestion and never an action: M3-01's rule is that a duplicate is never guessed at, because
 * merging two people who share a phone number is not undoable by the person who did it. The panel
 * shows that a suggestion exists and sends the agent to the administrator who can judge it.
 */
export function useMergeSuggestions(contactId: string | undefined) {
  return useQuery({
    queryKey: contactKeys.merges(contactId ?? 'none'),
    enabled: contactId !== undefined,
    queryFn: async () =>
      expectBody(
        await api.GET('/v1/contacts/{id}/merges', {
          params: { path: { id: contactId as string } },
        }),
      ).items,
  });
}

/**
 * The organisations a contact can be put in.
 *
 * Read by the panel only when the agent may actually write, because this is an administrator's list
 * and fetching it for somebody who cannot use it would be a request with no purpose.
 */
export function useOrganisations(enabled = true) {
  return useQuery({
    queryKey: contactKeys.organisations,
    enabled,
    queryFn: async () => expectBody(await api.GET('/v1/organisations')).items,
  });
}

/**
 * Giving the contact another identity to be recognised by.
 *
 * This is the edit an agent actually makes during a call: the person on the phone gives a second
 * number or an email address, and attaching it is what makes the next call resolve to them instead
 * of creating a stranger. It is additive and so it is safe — nothing is overwritten, and a mistake
 * is a spare identity rather than a lost one.
 */
export function useAttachIdentity(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AttachIdentityInput) =>
      expectBody(
        await api.POST('/v1/contacts/{id}/identities', {
          params: { path: { id: contactId } },
          body,
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: contactKeys.one(contactId) }),
  });
}

/** Putting the contact in an organisation, and optionally at one of its sites. */
export function useAssignOrganisation(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AssignOrganisationInput) =>
      expectBody(
        await api.POST('/v1/contacts/{id}/organisation', {
          params: { path: { id: contactId } },
          body,
        }),
      ),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: contactKeys.one(contactId) }),
  });
}

/**
 * Contacts by free text, for the command palette.
 *
 * Searched on the **server**, because a tenant's contacts are not in the browser to be filtered:
 * cmdk narrows what has arrived, and `/v1/contacts?q=` is what decides which contacts those are.
 * An empty query is not a search — it would be every contact the tenant has — so the hook is
 * disabled until the caller has something to ask for.
 */
export function useContactSearch(query: string, limit = 8) {
  const asked = query.trim();
  return useQuery({
    queryKey: contactKeys.search(asked),
    enabled: asked !== '',
    queryFn: async () =>
      expectBody(await api.GET('/v1/contacts', { params: { query: { q: asked, limit } } })).items,
  });
}
